/**
 * Activity Bubble Component for Rule Build
 *
 * Displays ephemeral activity status messages (THINKING, etc.) in chat stream
 * Ghost/outline style bubble with icon + text + spinner animation
 * Matches the styling from note-ai-edit's ActivityBubble
 */

import React, { memo } from 'react';
import { Avatar } from '@components/ui/chat';
import { Psychology as ThinkingIcon } from '@mui/icons-material';
import { Box, CircularProgress, Paper, Typography, useTheme } from '@mui/material';

export type ActivityType = 'THINKING' | 'PLAN' | 'SEARCH';

export interface ActivityBubbleProps {
  /** Activity type (determines icon) */
  activityType: ActivityType;
  /** Human-readable status message */
  message: string;
  /** Optional CSS class name */
  className?: string;
}

/**
 * Maps activity type to Material UI icon
 */
const getActivityIcon = (activityType: ActivityType): React.ReactElement => {
  switch (activityType) {
    case 'THINKING':
    case 'PLAN':
    case 'SEARCH':
    default:
      return <ThinkingIcon />;
  }
};

/**
 * Activity bubble component - ephemeral status indicator
 * Appears before assistant messages start streaming
 */
export const ActivityBubble = memo<ActivityBubbleProps>(
  ({ activityType, message, className }) => {
    const theme = useTheme();
    const icon = getActivityIcon(activityType);

    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: 1,
          animation: 'fadeIn 0.3s ease-in',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(10px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Avatar with activity icon */}
        {/* eslint-disable-next-line jsx-a11y/aria-role */}
        <Avatar role="assistant" icon={icon} />

        {/* Ghost-style activity bubble */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            maxWidth: '75%',
            bgcolor: 'transparent',
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            borderTopLeftRadius: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
          // ARIA live region for screen reader announcements
          aria-live="polite"
          aria-atomic="true"
        >
          {/* Activity message */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.875rem',
              fontStyle: 'italic',
            }}
          >
            {message}
          </Typography>

          {/* Spinner */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <CircularProgress size={14} thickness={4} sx={{ color: theme.palette.text.secondary }} />
          </Box>
        </Paper>
      </Box>
    );
  }
);

ActivityBubble.displayName = 'ActivityBubble';
